import React from 'react';
// eslint-disable-next-line no-unused-vars
import { motion } from 'framer-motion';
import { FaChild, FaBookOpen, FaUsers, FaBullhorn, FaHandsHelping, FaDonate, FaGraduationCap, FaHeartbeat, FaShieldAlt, FaHandHoldingHeart, FaUserFriends } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import './MissionPage.css';

// Animation variants
const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};

const fadeInLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};

const fadeInRight = {
    hidden: { opacity: 0, x: 60 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.2,
            delayChildren: 0.1
        }
    }
};

const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.5, ease: "easeOut" }
    }
};



const MissionPage = () => {
    const missionData = {
        statement: "To provide proper education to every under-privileged child of India and make them literate and increase their decision-making capacity.",
        goal: "The NGO aims at providing education to slum children and making them capable of differentiating between what is wrong and what is right.",
        objectives: [
            {
                icon: <FaGraduationCap />,
                title: "Increase Literacy",
                text: "Creating awareness amongst people about the importance of education and providing quality learning opportunities."
            },
            {
                icon: <FaHandHoldingHeart />,
                title: "Reduce Poverty",
                text: "Improving economic status of individuals through income generation activities and vocational training."
            },
            {
                icon: <FaHeartbeat />,
                title: "Improve Health Status",
                text: "Health education, environmental cleanliness, and increasing access to primary health and child care centers in remote villages."
            },
            {
                icon: <FaUsers />,
                title: "Women Empowerment",
                text: "Organizing women into self-help groups and developing their leadership qualities for decision making at personal and social levels."
            },
            {
                icon: <FaShieldAlt />,
                title: "Eliminate Child Labor",
                text: "Working to eliminate bonded, forced, child labor and slavery while promoting gender equality."
            },
            {
                icon: <FaChild />,
                title: "Drug Awareness",
                text: "Making children aware of the ill-effects of drugs and promoting healthy lifestyle choices."
            }
        ],
        approach: [
            {
                icon: <FaBookOpen />,
                title: "Education First",
                text: "We believe education is the foundation for all development. Our programs focus on making education accessible to slum children in Varanasi."
            },
            {
                icon: <FaUsers />,
                title: "Community-Based Approach",
                text: "Working directly with communities in slum areas to understand their needs and create sustainable solutions."
            },
            {
                icon: <FaHandsHelping />,
                title: "Holistic Development",
                text: "Addressing multiple aspects including education, health, vocational training, and personality development."
            },
            {
                icon: <FaUserFriends />,
                title: "Empowerment Focus",
                text: "Building decision-making capacity and life skills to help children differentiate between right and wrong."
            }
        ],
        heroSubtitle: "Dedicated to providing education and empowerment to underprivileged children in Varanasi since 2016."
    };

    return (
        <div className="mission-page">
            {/* Hero Section */}
            <motion.section
                className="mission-hero-section"
                initial="hidden"
                animate="visible"
                viewport={{ once: true }}
            >
                <div className="hero-background">
                    <img src="/images/IMG-20250605-WA0375.jpg" alt="Ummeed Ki Kiran Foundation Trust Mission" className="hero-bg-image" />
                    <div className="hero-overlay"></div>
                </div>
                <div className="hero-content-wrapper">
                    <motion.div className="container hero-content" variants={staggerContainer}>
                        <div className="hero-text">
                            <motion.h1 className="hero-title-mission" variants={fadeInUp}>
                                Our Mission
                            </motion.h1>
                            <motion.p className="hero-subtitle-mission" variants={fadeInUp}>
                                {missionData.heroSubtitle}
                            </motion.p>
                            <motion.div className="hero-highlights" variants={staggerContainer}>
                                <motion.div className="highlight-item" variants={scaleIn}>
                                    <FaGraduationCap className="highlight-icon" />
                                    <span>Education for All</span>
                                </motion.div>
                                <motion.div className="highlight-item" variants={scaleIn}>
                                    <FaUsers className="highlight-icon" />
                                    <span>Community Focus</span>
                                </motion.div>
                                <motion.div className="highlight-item" variants={scaleIn}>
                                    <FaHandsHelping className="highlight-icon" />
                                    <span>Holistic Development</span>
                                </motion.div>
                            </motion.div>
                        </div>
                    </motion.div>
                </div>
            </motion.section>

            {/* Mission Statement Section */}
            <motion.section
                className="section mission-statement-section"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="container">
                    <motion.h2 className="section-title text-center mb-8" variants={fadeInUp}>
                        Mission Statement
                    </motion.h2>
                    <motion.blockquote className="mission-quote" variants={fadeInUp}>
                        <p>"{missionData.statement}"</p>
                    </motion.blockquote>
                </div>
            </motion.section>

            {/* Goal Section */}
            <motion.section
                className="section goal-section bg-light-alt"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="container">
                    <motion.h2 className="section-title text-center" variants={fadeInUp}>
                        Our Goal
                    </motion.h2>
                    <div className="goal-content">
                        <motion.div className="goal-text" variants={fadeInLeft}>
                            <p className="goal-statement">{missionData.goal}</p>
                        </motion.div>
                        <motion.div className="goal-image" variants={fadeInRight}>
                            <img src="/images/IMG-20250605-WA0410.jpg" alt="Children Education - Ummeed Ki Kiran Foundation Trust" className="goal-img" />
                        </motion.div>
                    </div>
                </div>
            </motion.section>

            {/* Our Objectives Section */}
            <motion.section
                className="section objectives-section"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
            >
                <div className="container">
                    <motion.h2 className="section-title text-center" variants={fadeInUp}>
                        Our Objectives
                    </motion.h2>
                    <motion.p className="section-tagline text-center" variants={fadeInUp}>
                        The specific goals that drive our daily work and long-term vision.
                    </motion.p>
                    <motion.div className="objectives-grid" variants={staggerContainer}>
                        {missionData.objectives.map((objective, index) => (
                            <motion.div
                                key={index}
                                className="objective-card"
                                variants={scaleIn}
                                whileHover={{
                                    y: -10,
                                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                                    transition: { duration: 0.3 }
                                }}
                            >
                                <div className="objective-icon">{objective.icon}</div>
                                <h3 className="objective-title">{objective.title}</h3>
                                <p className="objective-text">{objective.text}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </motion.section>

            {/* Our Approach Section */}
            <motion.section
                className="section approach-section-mission bg-light-alt"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
            >
                <div className="container">
                    <motion.h2 className="section-title text-center" variants={fadeInUp}>
                        Our Approach
                    </motion.h2>
                    <motion.p className="section-tagline text-center" variants={fadeInUp}>
                        How we turn our mission into meaningful action for the children of Varanasi.
                    </motion.p>
                    <motion.div className="approach-grid-modern" variants={staggerContainer}>
                        {missionData.approach.map((item, index) => (
                            <motion.div
                                key={index}
                                className="approach-card-modern"
                                variants={scaleIn}
                                whileHover={{
                                    y: -10,
                                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                                    transition: { duration: 0.3 }
                                }}
                            >
                                <div className="approach-icon-wrapper">
                                    <motion.div
                                        className="approach-icon-modern"
                                        whileHover={{
                                            scale: 1.1,
                                            rotate: 5,
                                            transition: { duration: 0.2 }
                                        }}
                                    >
                                        {item.icon}
                                    </motion.div>
                                </div>
                                <div className="approach-content-modern">
                                    <h3 className="approach-title-modern">{item.title}</h3>
                                    <p className="approach-text-modern">{item.text}</p>
                                </div>
                                <div className="approach-number">{String(index + 1).padStart(2, '0')}</div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </motion.section>

            {/* Location and Focus Section */}
            <motion.section
                className="section location-section"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="container">
                    <motion.h2 className="section-title text-center" variants={fadeInUp}>
                        Area of Operation
                    </motion.h2>
                    <div className="location-content">
                        <motion.div className="location-text" variants={fadeInLeft}>
                            <h3>Varanasi District, Uttar Pradesh</h3>
                            <p>The Ummeed Ki Kiran Foundation Trust provides services specifically to <strong>slums of Varanasi district</strong>. We focus on reaching the most vulnerable and underserved children in these communities.</p>
                            <motion.ul className="location-highlights" variants={staggerContainer}>
                                <motion.li variants={fadeInUp}>Direct work with slum communities</motion.li>
                                <motion.li variants={fadeInUp}>Focus on underprivileged children</motion.li>
                                <motion.li variants={fadeInUp}>Community-based interventions</motion.li>
                                <motion.li variants={fadeInUp}>Sustainable local impact</motion.li>
                            </motion.ul>
                        </motion.div>
                        <motion.div className="location-image" variants={fadeInRight}>
                            <img src="/images/IMG-20250605-WA0396.jpg" alt="Ummeed Ki Kiran Foundation Trust Community Work" className="location-img" />
                        </motion.div>
                    </div>
                </div>
            </motion.section>

            {/* Call to Action Section */}
            <motion.section
                className="section cta-mission-section"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="container text-center">
                    <motion.h2 className="section-title-alt" variants={fadeInUp}>
                        Join Us in Our Mission
                    </motion.h2>
                    <motion.p className="section-tagline-alt" variants={fadeInUp}>
                        Your support can help us reach more children in Varanasi slums and create lasting change through education.
                    </motion.p>
                    <motion.div className="cta-buttons" variants={staggerContainer}>
                        <motion.div variants={scaleIn}>
                            <Link to="/donate" className="btn btn-primary btn-lg">
                                <FaDonate style={{ marginRight: '8px' }} /> Donate Now
                            </Link>
                        </motion.div>
                        {/* Commented out - no contact page available */}
                        {/* <motion.div variants={scaleIn}>
                            <Link to="/contact" className="btn btn-secondary btn-lg">
                                Get Involved
                            </Link>
                        </motion.div> */}
                    </motion.div>
                    <motion.div className="contact-info" variants={fadeInUp}>
                        <p><strong>Contact Us:</strong> +917985947303 | support@ummeedkikiran.org</p>
                    </motion.div>
                </div>
            </motion.section>
        </div>
    );
};

export default MissionPage;